import { useQuasar } from 'quasar';
import { useComponentDialog } from 'src/composables/componentDialog';
import { useActivityNavigation } from 'src/composables/useActivityNavigation';
import { notifyNegative } from 'src/composables/interactions';
import { defineAsyncComponent } from 'vue';

import { readSession, readSessionRelations } from '../../infrastructure/api/sessionApi';

const SessionActivityDialog = defineAsyncComponent(() => import('components/Sessions/Modals/SessionActivityDialog'));
const SessionCardDialog = defineAsyncComponent(() => import('components/Sessions/Modals/SessionCardDialog'));

/** @typedef {import('../../domain/entities/AuditTask.types').AuditTask} AuditTask */

/**
 * Composable that handles opening the correct dialog when viewing an audit task's details,
 * with back/next navigation across the audit task list. If the session activity is linked
 * to a session, loads the session with its programs and client before opening
 * SessionCardDialog. Otherwise, opens SessionActivityDialog directly.
 *
 * @param {() => AuditTask[]} getAuditTasks
 *   Getter returning the current, ordered audit tasks.
 * @returns {{
 * viewAuditTaskDetails: (auditTask: AuditTask) => Promise<void>
 * }}
 */
// eslint-disable-next-line import/prefer-default-export
export function useViewAuditTaskDetails(getAuditTasks = () => []) {
  const $q = useQuasar();
  const { openComponentDialog } = useComponentDialog();
  const activityNav = useActivityNavigation();

  function orderedActivities() {
    return (getAuditTasks() || [])
      .map((task) => task?.sessionActivity)
      .filter(Boolean)
      .map((sessionActivity) => ({
        id: sessionActivity.id,
        session: sessionActivity.session || null,
      }));
  }

  function openActivityDialog(row, navController) {
    return openComponentDialog(
      SessionActivityDialog,
      { sessionActivityId: row.id, applyImmediately: true, navController },
      () => {},
      null,
    );
  }

  async function openSessionDialog(row, navController) {
    let session;
    let relations;

    $q.loading.show();
    try {
      session = await readSession(row.session);
      if (session?.isDeleted) {
        session = null;
      }
      if (session) {
        relations = await readSessionRelations(session);
      }
    } catch (err) {
      notifyNegative(err.message);
      return null;
    } finally {
      $q.loading.hide();
    }

    if (!session) {
      return openActivityDialog(row, navController);
    }

    return openComponentDialog(
      SessionCardDialog,
      {
        allPrograms: relations.allPrograms,
        session,
        client: relations.client,
        openSessionActivity: row.id,
        navController,
      },
      () => {},
    );
  }

  activityNav.configure({
    source: { getRows: orderedActivities },
    openers: {
      activity: (row, controller) => openActivityDialog(row, controller),
      session: (row, controller) => openSessionDialog(row, controller),
    },
  });

  /**
   * @param {AuditTask} auditTask
   * @returns {Promise<void>}
   */
  function viewAuditTaskDetails(auditTask) {
    const { id: sessionActivityId, session: sessionId } = auditTask.sessionActivity;
    return activityNav.start({ id: sessionActivityId, session: sessionId || null });
  }

  return { viewAuditTaskDetails };
}
